import ChangeAccountDropdown from "components/ChangeAccountDropdown/ChangeAccountDropdown";
import PendingNotificationIcon from "components/PendingNotification/PendingNotification";
import { orderStatusConstants } from "constants/order";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { sizer } from "layout/styles/styled/sizer";
import { Analytics } from "pages/Analytics";
// import { Dashboard } from "pages/Dashboard";
import { OrdersTable } from "pages/Orders/components/OrdersTable";
import { PatientsTable } from "pages/Patients/components/PatientsTable";
import { Providers } from "pages/Providers";
import { ReportsTable } from "pages/Reports/component/ReportsTable";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import { Tooltip } from "primereact/tooltip";
import { useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useGetOrdersQuery } from "store/queries/order";
import { onAdminAccountChange, userSelector } from "store/slices/user";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";

import { PaginatedChatsTable } from "../Chatbot/components/PaginatedChatsTable";
import { ContractsLibrary } from "./components/ContractsLibrary";

// Tab name constants for named routing
const TAB_NAMES = {
  ORDERS: "orders",
  PROVIDERS: "providers",
  ANALYTICS: "analytics",
  PATIENTS: "patients",
  REPORTS: "reports",
  PATIENT_CHATS: "patient-chats",
  CONTRACTS: "contracts",
} as const;

const StyledContainer = styled.div`
  margin-bottom: ${sizer(8)};
`;

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

const StyledTabView = styled(TabView)<{ gray: boolean }>`
  .p-tabview-panels {
    ${(props) => props.gray && `background-color: #f5f5f5;`}
  }
`;

export default function ViewHospitals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { currentAccount: reduxCurrentAccount } = useSelector(userSelector);
  const { accounts, userInfo, userRefetch } = useUserWithAccount();

  // Extract tab and subtab from the pathname
  const pathParts = location.pathname.split("/").filter(Boolean);
  const idIndex = pathParts.findIndex((part) => part === id);
  const tab =
    idIndex >= 0 && pathParts[idIndex + 1] ? pathParts[idIndex + 1] : undefined;
  const subtab =
    idIndex >= 0 && pathParts[idIndex + 2] ? pathParts[idIndex + 2] : undefined;

  // Sync the Redux currentAccount state with the URL parameter IMMEDIATELY
  // This must happen synchronously before any child components render
  if (id && reduxCurrentAccount !== id) {
    dispatch(onAdminAccountChange({ currentAccount: id }));
  }

  const { data: currentPendingAmount } = useGetOrdersQuery({
    accountId: id,
    status: [orderStatusConstants.DRAFT],
  });

  const currentAccount: IAccount | undefined = accounts?.data.find(
    (aAccount) => aAccount.id === id
  );

  const filteredAccounts = useMemo(() => {
    if (userInfo?.scope === UserRole.SUPER_ADMIN) {
      return accounts?.data || [];
    }
    return (
      accounts?.data.filter((aAccount) =>
        userInfo?.accountIds.includes(aAccount.id)
      ) || []
    );
  }, [accounts, userInfo]);

  const showTabs = useMemo(() => {
    return userInfo?.scope === UserRole.SUPER_ADMIN;
  }, [userInfo]);

  // Build tab list based on permissions
  const availableTabs = useMemo(() => {
    // Contracts-only users (HospitalStaff with contractsOnlyMode) see ONLY Contracts tab
    if (
      userInfo?.scope === UserRole.HOSPITAL_STAFF &&
      userInfo?.contractsOnlyMode
    ) {
      return [TAB_NAMES.CONTRACTS];
    }

    // Normal tab visibility for all other users
    const tabs: any[] = [TAB_NAMES.ORDERS, TAB_NAMES.PROVIDERS];

    if (userInfo?.analyticsTabEnabled) {
      tabs.push(TAB_NAMES.ANALYTICS);
    }

    tabs.push(TAB_NAMES.PATIENTS, TAB_NAMES.REPORTS);

    if (showTabs && currentAccount?.patientChatBotId) {
      tabs.push(TAB_NAMES.PATIENT_CHATS);
    }

    // Contracts tab is always visible to all users
    tabs.push(TAB_NAMES.CONTRACTS);

    return tabs;
  }, [userInfo, showTabs, currentAccount]);

  // Get current tab from URL or default to orders
  const currentTabName = tab || TAB_NAMES.ORDERS;
  const activeIndex = availableTabs.indexOf(currentTabName);
  const activeTabIndex = activeIndex === -1 ? 0 : activeIndex;

  // Handler to update URL when tab changes
  const handleTabChange = (e: { index: number }) => {
    const newTabName = availableTabs[e.index];
    if (newTabName) {
      navigate(`/hospitals/view/${id}/${newTabName}`, { replace: true });
    }
  };

  // Build TabPanels array programmatically to match availableTabs exactly
  const tabPanels = useMemo(() => {
    const panels: JSX.Element[] = [];

    availableTabs.forEach((tabName) => {
      switch (tabName) {
        case TAB_NAMES.ORDERS:
          panels.push(
            <TabPanel key="orders" header="Orders">
              <OrdersTable
                accountId={id}
                directPayEnabled={currentAccount?.directPayEnabled}
                hstImportEnabled={currentAccount?.features?.hstImport}
                athenaImportEnabled={currentAccount?.features?.athenaImport}
              />
            </TabPanel>
          );
          break;
        case TAB_NAMES.PROVIDERS:
          panels.push(
            <TabPanel key="providers" header="Providers">
              <Providers />
            </TabPanel>
          );
          break;
        case TAB_NAMES.ANALYTICS:
          panels.push(
            <TabPanel key="analytics" header="Analytics">
              <Analytics accountId={id} />
            </TabPanel>
          );
          break;
        case TAB_NAMES.PATIENTS:
          panels.push(
            <TabPanel key="patients" header="Patients Across System">
              <PatientsTable showMessage />
            </TabPanel>
          );
          break;
        case TAB_NAMES.REPORTS:
          panels.push(
            <TabPanel key="reports" header="Reports">
              <ReportsTable accountId={id} />
            </TabPanel>
          );
          break;
        case TAB_NAMES.PATIENT_CHATS:
          panels.push(
            <TabPanel key="patient-chats" header="Patient Chats">
              <PaginatedChatsTable accountId={id} defaultPageSize={20} />
            </TabPanel>
          );
          break;
        case TAB_NAMES.CONTRACTS:
          panels.push(
            <TabPanel key="contracts" header="Contracts">
              <ContractsLibrary
                accountId={id!}
                accountName={currentAccount?.name}
                subtab={subtab}
              />
            </TabPanel>
          );
          break;
        default:
          break;
      }
    });

    return panels;
  }, [availableTabs, id, currentAccount, subtab]);

  return (
    <>
      {userInfo?.scope === UserRole.SUPER_ADMIN ? (
        <StyledButton
          icon="pi pi-arrow-left"
          onClick={() => navigate("/hospitals")}
          className="p-button-text p-button-secondary p-0"
        >
          <p className="small-text">Return to Hospitals</p>
        </StyledButton>
      ) : null}
      <StyledContainer className="flex flex-wrap gap-6 align-items-center">
        <h2>{currentAccount?.name}</h2>
        <div className="flex gap-4 align-items-center">
          {filteredAccounts.length > 1 ? (
            <ChangeAccountDropdown
              defaultSelected={currentAccount?.name || null}
              accounts={filteredAccounts}
              userDefaultAccount={userInfo?.defaultAccount || null}
              userId={userInfo?.id || null}
              onUserRefetch={userRefetch}
            />
          ) : null}
          {currentPendingAmount?.data?.length ? (
            <>
              <Tooltip
                style={{
                  fontSize: "14px",
                }}
                target=".pending-notification"
              />
              <PendingNotificationIcon
                count={currentPendingAmount?.data?.length || 0}
                className="pending-notification cursor-pointer"
                tooltipText="Orders Pending Review"
                tooltipPosition="right"
              />
            </>
          ) : null}
        </div>
      </StyledContainer>

      <StyledTabView
        activeIndex={activeTabIndex}
        className="w-full"
        onTabChange={handleTabChange}
        gray={false}
      >
        {tabPanels}
      </StyledTabView>
    </>
  );
}
